import React, { ReactNode } from "react";
import classNames from "classnames";

import { Carousel } from "../../Shared/Carousel/Carousel";
import { ExhibitionCarousel } from "../../../../Blocks/ImageCarousel/ExhibitionCarousel/ExhibitionCarousel";

export type BackgroundColor = "green" | "blue" | "red" | "white" | "black" | "pink" | "blackGold" | "goldBlack";

export type Theme =
    | "goldBlue"
    | "blackRed"
    | "pinkBlue"
    | "greenWhite"
    | "blueWhite"
    | "blueRed"
    | "redBlack"
    | "redBlue"
    | "pinkBlack"
    | "blackGold"
    | "blackWhite"
    | "goldBlack"
    | "whiteBlack"
    | "whiteRed"
    | "whiteGreen"
    | "whiteBlue"
    | "goldWhite"
    | "bluePink"
    | "greenPink"
    | "greyRed";

interface ICarouselBlock {
    title: string;
    buttonText?: string;
    buttonUrl?: string;
    bgColor?: BackgroundColor;
    theme?: string;
    customBgColor?: string;
    customFontColor?: string;
    displayData: { key: string; data: ReactNode }[];
    previousSlideAriaLabel: string;
    nextSlideAriaLabel: string;
    className?: string;
    fullWidth?: boolean;
    itemsPerSlideDesktop?: number | "auto";
    itemsPerSlideMobile?: number | "auto";
    isExhibition?: boolean;
}

const bgColorStyles: Record<BackgroundColor, string> = {
    green: "gold",
    blue: "pink",
    red: "white",
    white: "black",
    black: "white",
    pink: "blue",
    blackGold: "gold",
    goldBlack: "black",
};

function extractLastColorFromTheme(theme: string): string {
    const words = theme.match(/[A-Z]?[a-z]+/g);
    if (words && words.length > 0) {
        return words[words.length - 1].toLowerCase();
    }
    return "white";
}

export const CarouselBlock = ({
    title,
    buttonText,
    buttonUrl,
    bgColor,
    theme,
    customBgColor,
    customFontColor,
    displayData,
    previousSlideAriaLabel,
    nextSlideAriaLabel,
    className,
    fullWidth = false,
    itemsPerSlideDesktop,
    itemsPerSlideMobile,
    isExhibition = false,
}: ICarouselBlock) => {
    if (displayData.length === 0) return null;

    let arrowColor = bgColor === "white" ? "black" : "white";
    let containerClassName = "";

    if (theme && theme !== "custom") {
        containerClassName = `CarouselBlock--${theme}`;
        arrowColor = extractLastColorFromTheme(theme);
    } else {
        if (bgColor) {
            containerClassName = `CarouselBlock--${bgColor}`;
            arrowColor = bgColorStyles[bgColor];
        }

        if (customFontColor) {
            arrowColor = customFontColor;
        }
    }

    return (
        <div className={classNames("CarouselBlock__outer", { "CarouselBlock__outer--full-width": fullWidth })}>
            <div
                className={classNames("CarouselBlock", containerClassName, className)}
                style={{
                    ...(customBgColor && { backgroundColor: customBgColor }),
                    ...(customFontColor && { color: customFontColor }),
                }}
            >
                <div className="CarouselBlock__Wrapper">
                    <div className="CarouselBlock__Title__Container">
                        <h2
                            className={classNames(
                                "CarouselBlock__Title",
                                !theme && bgColor && `CarouselBlock__Title--${bgColorStyles[bgColor]}`
                            )}
                        >
                            {title}
                        </h2>
                    </div>
                    <div className="CarouselBlock__Items">
                        {isExhibition ? (
                            <ExhibitionCarousel
                                previousSlideAriaLabel={previousSlideAriaLabel}
                                nextSlideAriaLabel={nextSlideAriaLabel}
                                displayData={displayData.map((item, index) => ({
                                    key: `item-${index}`,
                                    data: item.data,
                                }))}
                                arrowColor={arrowColor}
                                customArrowColor={customFontColor}
                            />
                        ) : (
                            <Carousel
                                previousSlideAriaLabel={previousSlideAriaLabel}
                                nextSlideAriaLabel={nextSlideAriaLabel}
                                spaceBetweenSlides={20}
                                displayData={displayData.map((item, index) => ({
                                    key: `item-${index}`,
                                    data: item.data,
                                }))}
                                itemsPerSlideDesktop={itemsPerSlideDesktop}
                                itemsPerSlideMobile={itemsPerSlideMobile}
                                arrowColor={arrowColor}
                            />
                        )}
                    </div>

                    {buttonUrl && buttonText && (
                        <div className="CarouselBlock__Button">
                            <a className="button--secondary button--white" href={buttonUrl}>
                                {buttonText}
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
